import React, { useState, useEffect, useContext, useRef } from "react";
import { SessionContext } from "../../components/SessionContext";
import TopBar from "../../components/TopBar";
import { useNavigate } from "react-router-dom";
import supabase from "../../supabase";
import moment from "moment";
import { toast } from "react-toastify";
import { 
  FiSearch, FiTrash2, FiEye, FiChevronDown, 
  FiChevronLeft, FiChevronRight, FiCopy, FiCheck, FiFilter,
  FiArrowUp, FiArrowDown, FiRotateCcw, FiCalendar, FiGrid, FiXCircle
} from "react-icons/fi";
import { 
  LuShoppingBag, LuTrendingUp, LuPackage 
} from "react-icons/lu";
import { IoLogoWhatsapp } from "react-icons/io5";
import { LiaShippingFastSolid } from "react-icons/lia";
import { TbDeviceFloppy, TbPrinter } from "react-icons/tb";
import { sendWhatsAppNotification } from "../../services/whatsappService";

const Orders = () => {
  const { session } = useContext(SessionContext);
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [allOrdersForKPIs, setAllOrdersForKPIs] = useState([]);
  const [blockedPhones, setBlockedPhones] = useState(new Set());
  const [status, setStatus] = useState(localStorage.getItem("activeOrderStatus") || "all");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchBasis, setSearchBasis] = useState("name");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 50;

  // Selected orders for bulk actions
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [bulkActionOpen, setBulkActionOpen] = useState(false);
  const [isBulkCreatingShipment, setIsBulkCreatingShipment] = useState(false);
  const [orderTrackingInputs, setOrderTrackingInputs] = useState({});
  const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(null);
  const [isSavingTracking, setIsSavingTracking] = useState(null);
  const [copiedOrderId, setCopiedOrderId] = useState(null);
  const [moreActionsOpen, setMoreActionsOpen] = useState(false);
  const [showStatsGrid, setShowStatsGrid] = useState(true);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);

  // Cancellation Modal & Restocking State
  const [cancelModalOrder, setCancelModalOrder] = useState(null);
  const [previewOrder, setPreviewOrder] = useState(null);
  const [isRestocking, setIsRestocking] = useState(false);

  // Sorting state
  const [sortColumn, setSortColumn] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");

  // Timeframe state for KPI graphs
  const [timeframeDays, setTimeframeDays] = useState(7);
  const [timeframeOpen, setTimeframeOpen] = useState(false);
  const timeframeRef = useRef(null);
  const timeframeOptions = [
    { label: "Today", days: 1 },
    { label: "Yesterday", days: "yesterday" },
    { label: "Last 7 days", days: 7 },
    { label: "Last 30 days", days: 30 },
    { label: "Last 90 days", days: 90 },
    { label: "All time", days: 3650 },
  ];

  const normalizePhone = (phone) => {
    if (!phone) return "";
    const clean = String(phone).replace(/\D/g, "");
    return clean.length >= 10 ? clean.slice(-10) : clean;
  };

  const fetchOrders = async () => {
    // 1. Fetch blocked phone numbers first
    const { data: blockedData, error: blockedError } = await supabase
      .from("orders")
      .select("*")
      .eq("order_status", "cancelled_blocked");

    if (blockedError) {
      console.error("Error fetching blocked orders list:", blockedError);
    }

    const blockedPhonesLocal = new Set();
    if (!blockedError && blockedData) {
      blockedData.forEach(o => {
        const p1 = normalizePhone(o.phone);
        const p2 = normalizePhone(o.address?.phone);
        if (p1) blockedPhonesLocal.add(p1);
        if (p2) blockedPhonesLocal.add(p2);
      });
    }
    setBlockedPhones(blockedPhonesLocal);

    let query = supabase
      .from("orders")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false });

    if (status === "waiting") {
      query = query.eq("order_status", "waiting").is("waybill", null);
    } else if (status === "ready_to_pickup") {
      query = query.eq("order_status", "waiting").not("waybill", "is", null);
    } else if (status === "cancelled") {
      // Fetch all cancelled/blocked status, and since we might have active orders from blocked users,
      // we don't restrict by order_status here to fetch all orders client-side
    } else if (status !== "all") {
      query = query.eq("order_status", status);
    }

    if (status === "all" && !searchTerm) {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error("Error fetching orders: ", error);
      toast.error("Failed to fetch orders!");
    } else {
      const filteredData = data.filter(
        (order) => {
          const isPaymentValid =
            (order.payment_method === "Razorpay" && order.razorpay_payment_id) ||
            order.payment_method === "COD" ||
            order.payment_method === "ONLINE" ||
            order.payment_method === "Prepaid" ||
            !order.payment_method;

          if (!isPaymentValid) return false;

          const p1 = normalizePhone(order.phone);
          const p2 = normalizePhone(order.address?.phone);
          const isBlocked = blockedPhonesLocal.has(p1) || blockedPhonesLocal.has(p2) || order.order_status === "cancelled_blocked";

          // If the order is blocked (or the customer is blocked), it should ONLY appear in the "Cancelled" and "All" sections!
          if (isBlocked) {
            if (status === "cancelled" || status === "all") {
              return true;
            }
            return false;
          }

          // In the "Cancelled" tab, show admin-cancelled, keep-stock cancelled, and blocked cancelled
          if (status === "cancelled") {
            return order.order_status === "cancelled_by_admin" || order.order_status === "cancelled_keep_stock" || order.order_status === "cancelled_blocked";
          }

          // For all other sections/tabs, hide customer-cancelled ("cancelled")
          return order.order_status !== "cancelled";
        }
      );
      setOrders(filteredData);
      if (count !== null) setTotalCount(count);
    }
  };

  // Fetch ALL orders (no tab/pagination filter) purely for KPI metrics
  const fetchAllOrdersForKPIs = async () => {
    const { data, error } = await supabase
      .from("orders")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching KPI orders:", error);
    }

    if (!error && data) {
      const blockedPhonesLocal = new Set();
      data.forEach(o => {
        if (o.order_status === "cancelled_blocked") {
          const p1 = normalizePhone(o.phone);
          const p2 = normalizePhone(o.address?.phone);
          if (p1) blockedPhonesLocal.add(p1);
          if (p2) blockedPhonesLocal.add(p2);
        }
      });
      setBlockedPhones(blockedPhonesLocal);

      // Filter out incomplete Razorpay orders (started but not paid) and blocked orders
      const valid = data.filter(
        (order) => {
          const isPaymentValid =
            ((order.payment_method === "Razorpay" && order.razorpay_payment_id) ||
            order.payment_method === "COD" ||
            order.payment_method === "ONLINE" ||
            order.payment_method === "Prepaid" ||
            !order.payment_method);

          if (!isPaymentValid) return false;

          const p1 = normalizePhone(order.phone);
          const p2 = normalizePhone(order.address?.phone);
          const isBlocked = blockedPhonesLocal.has(p1) || blockedPhonesLocal.has(p2) || order.order_status === "cancelled_blocked";

          return !isBlocked;
        }
      );
      setAllOrdersForKPIs(valid);
    }
  };

  useEffect(() => {
    fetchOrders();
    const intervalId = setInterval(fetchOrders, 300000); // 5 minutes refresh
    localStorage.setItem("activeOrderStatus", status);
    return () => clearInterval(intervalId);
  }, [status, page, searchTerm]);

  // Separate fetch for KPI data — runs on mount, when session loads, and when timeframe changes
  useEffect(() => {
    if (session) {
      fetchAllOrdersForKPIs();
    }
  }, [session, timeframeDays]);

  // Close timeframe dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (timeframeRef.current && !timeframeRef.current.contains(e.target)) {
        setTimeframeOpen(false);
      }
    };
    if (timeframeOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [timeframeOpen]);

  const cycleFulfillmentStatus = async (e, order) => {
    e.stopPropagation(); // Prevent navigating to order details page
    
    // Cycle sequence: waiting (Unfulfilled) -> shipped -> ready_to_pickup -> delivered -> cancelled_by_admin -> waiting
    let nextStatus = "waiting";
    if (order.order_status === "waiting" || !order.order_status) {
      nextStatus = "shipped";
    } else if (order.order_status === "shipped") {
      nextStatus = "ready_to_pickup";
    } else if (order.order_status === "ready_to_pickup") {
      nextStatus = "delivered";
    } else if (order.order_status === "delivered") {
      nextStatus = "cancelled_by_admin";
    } else if (order.order_status === "cancelled_by_admin" || order.order_status === "cancelled" || order.order_status === "cancelled_keep_stock") {
      nextStatus = "waiting";
    }

    if (nextStatus === "cancelled_by_admin") {
      setCancelModalOrder(order);
      return;
    }

    try {
      const updatePayload = { order_status: nextStatus };
      if (nextStatus !== "waiting") {
        updatePayload.hold_status = null;
      }

      const { error } = await supabase
        .from("orders")
        .update(updatePayload)
        .eq("order_id", order.order_id);

      if (error) {
        toast.error("Failed to update status: " + error.message);
      } else {
        const displayStatusName = nextStatus === "waiting" 
          ? "Unfulfilled" 
          : nextStatus === "cancelled_by_admin" 
          ? "Cancelled & Released Stock" 
          : nextStatus;
        toast.success(`Order #${order.order_id_seq || order.order_id} status updated to ${displayStatusName}!`);
        // Update local state immediately
        setOrders(prevOrders => 
          prevOrders.map(o => o.order_id === order.order_id ? { ...o, order_status: nextStatus, hold_status: nextStatus !== "waiting" ? null : o.hold_status } : o)
        );
        fetchAllOrdersForKPIs();
      }
    } catch (err) {
      toast.error("An error occurred while cycling status.");
    }
  };

  const executeCancellation = async (order, restock = false, block = false) => {
    setIsRestocking(true);
    const targetStatus = block ? "cancelled_blocked" : restock ? "cancelled_keep_stock" : "cancelled_by_admin";
    
    try {
      if (restock || block) {
        toast.info("Restocking product inventory...");
        const itemsArray = Array.isArray(order.items) ? order.items : Object.values(order.items || {});
        for (const item of itemsArray) {
          if (!item.id) continue;
          
          // 1. Fetch current product
          const { data: product, error: fetchError } = await supabase
            .from("products")
            .select("id, stock, stockMap")
            .eq("id", item.id)
            .maybeSingle();

          if (fetchError || !product) {
            console.error(`Product not found or error fetching for ID ${item.id}:`, fetchError);
            continue;
          }

          // 2. Increment stock based on size or general stock
          if (item.size && product.stockMap) {
            const sizeKey = String(item.size);
            const currentStockMap = { ...product.stockMap };
            const currentSizeStock = Number(currentStockMap[sizeKey]) || 0;
            currentStockMap[sizeKey] = currentSizeStock + (Number(item.quantity) || 1);

            const { error: updateError } = await supabase
              .from("products")
              .update({ stockMap: currentStockMap })
              .eq("id", product.id);
            if (updateError) {
              console.error(`Error updating stockMap for product ${product.id}:`, updateError);
            }
          } else {
            const currentStock = Number(product.stock) || 0;
            const newStock = currentStock + (Number(item.quantity) || 1);

            const { error: updateError } = await supabase
              .from("products")
              .update({ stock: newStock })
              .eq("id", product.id);
            if (updateError) {
              console.error(`Error updating general stock for product ${product.id}:`, updateError);
            }
          }
        }
      }

      // 3. Update the order status in the database
      const { error: orderError } = await supabase
        .from("orders")
        .update({ order_status: targetStatus, hold_status: null })
        .eq("order_id", order.order_id);

      if (orderError) {
        toast.error("Failed to cancel order: " + orderError.message);
      } else {
        toast.success(block ? `Order #${order.order_id_seq || order.order_id} cancelled and user blocked!` : `Order #${order.order_id_seq || order.order_id} successfully cancelled!`);
        // Update local state immediately
        setOrders(prevOrders => 
          prevOrders.map(o => o.order_id === order.order_id ? { ...o, order_status: targetStatus, hold_status: null } : o)
        );
        fetchAllOrdersForKPIs();
      }
    } catch (err) {
      console.error("Error during cancellation restock execution:", err);
      toast.error("An unexpected error occurred during cancellation.");
    } finally {
      setIsRestocking(false);
      setCancelModalOrder(null);
    }
  };

  const filteredOrders = orders.filter((order) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase().trim();
    const address = order.address || {};
    const { name, phone, whatsapp, zip } = address;

    switch (searchBasis) {
      case "name":
        return name && name.toLowerCase().includes(term);
      case "phone":
        return (phone && phone.includes(term)) || (order.phone && order.phone.includes(term));
      case "order_id":
        const seqStr = order.order_id_seq ? String(order.order_id_seq).toLowerCase() : "";
        const idStr = order.order_id ? String(order.order_id).toLowerCase() : "";
        return seqStr.includes(term) || idStr.includes(term);
      case "pincode":
        return zip && zip.includes(term);
      case "whatsapp":
        return whatsapp && whatsapp.includes(term);
      default:
        return true;
    }
  });

  // Sort handler
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Sorted orders
  const sortedOrders = [...filteredOrders].sort((a, b) => {
    const dir = sortDirection === "asc" ? 1 : -1;
    switch (sortColumn) {
      case "order": {
        const aVal = a.order_id_seq || 0;
        const bVal = b.order_id_seq || 0;
        return (aVal - bVal) * dir;
      }
      case "date":
        return (new Date(a.created_at) - new Date(b.created_at)) * dir;
      case "customer": {
        const aName = (a.address?.name || "").toLowerCase();
        const bName = (b.address?.name || "").toLowerCase();
        return aName.localeCompare(bName) * dir;
      }
      case "payment": {
        const aP = a.payment_method === "COD" ? "Cash pending" : "Paid";
        const bP = b.payment_method === "COD" ? "Cash pending" : "Paid";
        return aP.localeCompare(bP) * dir;
      }
      case "fulfillment":
        return (a.order_status || "").localeCompare(b.order_status || "") * dir;
      case "items": {
        const aItems = Array.isArray(a.items) ? a.items : Object.values(a.items || {});
        const bItems = Array.isArray(b.items) ? b.items : Object.values(b.items || {});
        const aCount = aItems.reduce((acc, i) => acc + (Number(i?.quantity) || 1), 0);
        const bCount = bItems.reduce((acc, i) => acc + (Number(i?.quantity) || 1), 0);
        return (aCount - bCount) * dir;
      }
      case "total":
        return ((Number(a.amount) || 0) - (Number(b.amount) || 0)) * dir;
      default:
        return 0;
    }
  });

  const handleDeleteOrder = async (e, order) => {
    if (e) e.stopPropagation();
    const confirmDelete = window.confirm(`Are you sure you want to delete order #${order.order_id_seq || order.order_id}? This action cannot be undone.`);
    if (!confirmDelete) return;

    try {
      const { error } = await supabase
        .from("orders")
        .delete()
        .eq("order_id", order.order_id);

      if (error) {
        toast.error("Failed to delete order: " + error.message);
      } else {
        toast.success("Order deleted successfully!");
        setOrders(orders.filter((o) => o.order_id !== order.order_id));
        setSelectedOrderIds(selectedOrderIds.filter(id => id !== order.order_id));
      }
    } catch (err) {
      toast.error("An unexpected error occurred while deleting.");
    }
  };

  const handleUndoCancel = async (e, order) => {
    if (e) e.stopPropagation();
    const confirmUndo = window.confirm(`Are you sure you want to undo cancellation for order #${order.order_id_seq || order.order_id}?`);
    if (!confirmUndo) return;

    try {
      const wasRestocked = order.order_status === "cancelled_keep_stock" || order.order_status === "cancelled_blocked";
      
      if (wasRestocked) {
        toast.info("Re-deducting stock inventory for reactivated order...");
        const itemsArray = Array.isArray(order.items) ? order.items : Object.values(order.items || {});
        for (const item of itemsArray) {
          if (!item.id) continue;
          
          // 1. Fetch current product
          const { data: product, error: fetchError } = await supabase
            .from("products")
            .select("id, stock, stockMap")
            .eq("id", item.id)
            .maybeSingle();

          if (fetchError || !product) {
            console.error(`Product not found or error fetching for ID ${item.id}:`, fetchError);
            continue;
          }

          // 2. Decrement stock based on size or general stock
          if (item.size && product.stockMap) {
            const sizeKey = String(item.size);
            const currentStockMap = { ...product.stockMap };
            const currentSizeStock = Number(currentStockMap[sizeKey]) || 0;
            currentStockMap[sizeKey] = Math.max(0, currentSizeStock - (Number(item.quantity) || 1));

            const { error: updateError } = await supabase
              .from("products")
              .update({ stockMap: currentStockMap })
              .eq("id", product.id);
            if (updateError) {
              console.error(`Error updating stockMap for product ${product.id}:`, updateError);
            }
          } else {
            const currentStock = Number(product.stock) || 0;
            const newStock = Math.max(0, currentStock - (Number(item.quantity) || 1));

            const { error: updateError } = await supabase
              .from("products")
              .update({ stock: newStock })
              .eq("id", product.id);
            if (updateError) {
              console.error(`Error updating general stock for product ${product.id}:`, updateError);
            }
          }
        }
      }

      // 3. Update order status to waiting
      const { error } = await supabase
        .from("orders")
        .update({ order_status: "waiting" })
        .eq("order_id", order.order_id);

      if (error) {
        toast.error("Failed to undo cancellation: " + error.message);
      } else {
        toast.success("Order cancellation undone successfully!");
        // Update local state immediately
        setOrders(prevOrders => 
          prevOrders.map(o => o.order_id === order.order_id ? { ...o, order_status: "waiting" } : o)
        );
        fetchAllOrdersForKPIs();
      }
    } catch (err) {
      console.error("Error during undo cancellation execution:", err);
      toast.error("An error occurred while undoing cancellation.");
    }
  };

  // Bulk Actions handlers
  const handleBulkDelete = async () => {
    const confirmDelete = window.confirm(`Are you sure you want to delete ${selectedOrderIds.length} selected orders?`);
    if (!confirmDelete) return;

    try {
      const { error } = await supabase
        .from("orders")
        .delete()
        .in("order_id", selectedOrderIds);

      if (error) {
        toast.error("Failed to delete selected orders: " + error.message);
      } else {
        toast.success("Selected orders deleted successfully!");
        setOrders(orders.filter((o) => !selectedOrderIds.includes(o.order_id)));
        setSelectedOrderIds([]);
      }
    } catch (err) {
      toast.error("An error occurred during bulk delete.");
    }
    setBulkActionOpen(false);
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    const confirmUpdate = window.confirm(`Update status of ${selectedOrderIds.length} orders to '${newStatus === "cancelled_by_admin" ? "Cancelled & Released Stock" : newStatus}'?`);
    if (!confirmUpdate) return;

    try {
      const { error } = await supabase
        .from("orders")
        .update({ order_status: newStatus })
        .in("order_id", selectedOrderIds);

      if (error) {
        toast.error("Failed to update status: " + error.message);
      } else {
        const displayStatus = newStatus === "cancelled_by_admin" ? "Cancelled & Released Stock" : newStatus;
        toast.success(`Selected orders marked as ${displayStatus}!`);
        setOrders(
          orders.map((o) =>
            selectedOrderIds.includes(o.order_id)
              ? { ...o, order_status: newStatus }
              : o
          )
        );
        setSelectedOrderIds([]);
      }
    } catch (err) {
      toast.error("An error occurred during bulk update.");
    }
    setBulkActionOpen(false);
  };

  // Bulk shipment creation
  const handleBulkShipment = async () => {
    if (isBulkCreatingShipment) return;

    const selectedOrders = orders.filter((o) => selectedOrderIds.includes(o.order_id));
    if (selectedOrders.length === 0) {
      toast.warning("Please select at least one order to create shipment.");
      return;
    }

    const eligibleOrders = selectedOrders.filter((o) => !o.waybill);
    if (eligibleOrders.length === 0) {
      toast.info("All selected orders already have a waybill/shipment!");
      return;
    }

    const alreadyShippedCount = selectedOrders.length - eligibleOrders.length;
    let confirmMsg = `Create shipment for ${eligibleOrders.length} selected order(s)?`;
    if (alreadyShippedCount > 0) {
      confirmMsg += `\n(${alreadyShippedCount} order(s) already have a waybill and will be skipped)`;
    }

    if (!window.confirm(confirmMsg)) return;

    setIsBulkCreatingShipment(true);
    let successCount = 0;
    let failCount = 0;
    const newWaybillMap = {};

    try {
      const authToken =
        session?.access_token ||
        (await supabase.auth.getSession())?.data?.session?.access_token;

      for (let i = 0; i < eligibleOrders.length; i++) {
        const order = eligibleOrders[i];
        try {
          const response = await fetch(
            `https://ecvdsyezeauzidjqmpyc.supabase.co/functions/v1/shipment-creation?orderId=${order.order_id}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
              },
            }
          );

          const data = await response.json();
          if (response.ok && data.waybill) {
            successCount++;
            newWaybillMap[order.order_id] = data.waybill;
          } else {
            failCount++;
            console.error(
              `Error creating shipment for order #${order.order_id_seq || order.order_id}:`,
              data
            );
            toast.error(
              `Order #${order.order_id_seq || order.order_id}: ${data.error_message || data.error || "Failed to create shipment"}`
            );
          }
        } catch (err) {
          failCount++;
          console.error(`Network error for order #${order.order_id_seq || order.order_id}:`, err);
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully created shipment for ${successCount} order(s)!`);
        setOrders((prev) =>
          prev.map((o) => {
            if (newWaybillMap[o.order_id]) {
              return {
                ...o,
                waybill: newWaybillMap[o.order_id],
                order_status: o.order_status === "waiting" ? "ready_to_pickup" : o.order_status,
              };
            }
            return o;
          })
        );
        setSelectedOrderIds([]);
        fetchAllOrdersForKPIs();
      }

      if (failCount > 0 && successCount === 0) {
        toast.error(`Failed to create shipments for selected orders.`);
      }
    } catch (err) {
      console.error("Bulk shipment error:", err);
      toast.error("An error occurred during bulk shipment creation.");
    } finally {
      setIsBulkCreatingShipment(false);
    }
  };

  // Print Taqwa Invoices helper (for single order or bulk)
  const printInvoices = async (ordersToPrint) => {
    if (!ordersToPrint || ordersToPrint.length === 0) {
      toast.warning("Please select at least one order to print invoices.");
      return;
    }

    // Fetch user postoffice names if user_ids exist
    const userIds = [...new Set(ordersToPrint.map((o) => o.user_id).filter(Boolean))];
    let userMap = {};
    if (userIds.length > 0) {
      try {
        const { data: usersData, error: usersErr } = await supabase
          .from("users")
          .select("id, postoffice_name")
          .in("id", userIds);
        if (!usersErr && usersData) {
          usersData.forEach((u) => {
            userMap[u.id] = u;
          });
        }
      } catch (e) {
        console.warn("Could not fetch user postoffice names:", e);
      }
    }

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Popup blocked! Please allow popups for this site to print invoices.");
      return;
    }

    const pagesHtml = ordersToPrint.map((order) => {
      const itemsArray = Array.isArray(order.items) ? order.items : Object.values(order.items || {});
      const isCod = order.payment_method === "COD";
      const dateStr = moment(order.created_at).format("DD-MM-YYYY");
      const timeStr = moment(order.created_at).format("HH:mm");
      const user = userMap[order.user_id];
      const postofficeName = user?.postoffice_name || order.address?.postoffice_name;

      const isPerfumes =
        (order.template_type && order.template_type.toLowerCase().includes("perfume")) ||
        (order.store_name && order.store_name.toLowerCase().includes("perfume")) ||
        itemsArray.some(
          (item) =>
            (item?.name && item.name.toLowerCase().includes("perfume")) ||
            (item?.category && item.category.toLowerCase().includes("perfume"))
        );
      const storeName = isPerfumes ? "TAQWA PERFUMES" : "TAQWA FASHION STORE";

      const productsHtml = itemsArray
        .map((item) => {
          const sizeStr = item?.size ? ` [${item.size}]` : "";
          const price = Number(item.priceMap ? item.priceMap[item.size] : item.price) || 0;
          const qty = Number(item.quantity) || 1;
          if (isCod) {
            return `
              <tr>
                <td>${item?.name || "Product"}${sizeStr} (${qty})</td>
                <td style="text-align: center;">${price}</td>
                <td style="text-align: center; border-right: none;">${price * qty}</td>
              </tr>
            `;
          } else {
            return `
              <tr>
                <td>${item?.name || "Product"}${sizeStr} (${qty})</td>
                <td style="text-align: center; border-right: none;">${price * qty}</td>
              </tr>
            `;
          }
        })
        .join("");

      if (isCod) {
        return `
          <div class="label-page cod">
            <div class="label-wrapper">
              <!-- 1. STORE HEADER -->
              <div class="row row-header">
                <div class="header-full">
                  <div class="store-name">${storeName}</div>
                </div>
              </div>

              <!-- 2. PINCODE / ROUTE -->
              <div class="row row-pin">
                <div class="pin-left">PINCODE: ${order.address?.zip || ""}</div>
                <div class="route-right">COD / SPEEDPOST</div>
              </div>

              <!-- 3. SHIP TO + COD BOX -->
              <div class="row row-shipto">
                <div class="shipto-cell">
                  <div>
                    <div style="font-size: 8pt; font-weight: 700; margin-bottom: 2px;">SHIP TO:</div>
                    <div class="shipto-name">${order.address?.name || "Customer"}</div>
                    <div class="shipto-addr-heavy">
                      ${order.address?.address || ""}<br>
                      ${order.address?.place ? order.address.place + "<br>" : ""}
                      ${postofficeName ? "Postoffice Name: " + postofficeName + "<br>" : ""}
                      ${order.address?.city || ""}, ${order.address?.state || ""}<br>
                      PIN: ${order.address?.zip || ""}
                    </div>
                  </div>
                  <div style="font-size: 13pt; font-weight: 900; margin-top: 4px; line-height: 1.2;">
                    PH: ${order.address?.phone || order.phone || ""}
                  </div>
                </div>

                <div class="cod-cell">
                  <div class="cod-amount-title">COD AMOUNT</div>
                  <div style="font-size: 20pt; font-weight: 900; line-height: 1;">
                    ${order.amount}
                  </div>
                  
                  <div style="border: 1.5px solid #000; padding: 2px 4px; margin-top: 6px; width: 100%;">
                    <div style="font-size: 6.5pt; font-weight: 900; margin-bottom: -2px;">E-BILLER ID</div>
                    <div style="font-size: 13pt; font-weight: 900; letter-spacing: 1px;">55938</div>
                  </div>
                </div>
              </div>

              <!-- 4. SELLER + ORDER DATE -->
              <div class="row row-seller-date">
                <div class="seller-cell">
                  <div style="font-size: 8pt; font-weight: 700; margin-bottom: 2px;">SELLER:</div>
                  <div class="shipto-name" style="font-size: 9pt;">${storeName}</div>
                  <div class="seller-addr-bold">
                    PH: 7012556844<br>
                    KANJIRAMATTOM, KERALA<br>
                    INDIA 682315
                  </div>
                </div>

                <div style="width: 35%; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center;">
                  <div style="font-size: 8pt; font-weight: 700;">ORDER DATE</div>
                  <div style="font-size: 9pt; font-weight: 900;">
                    ${dateStr}<br>
                    ${timeStr}
                  </div>
                </div>
              </div>

              <!-- 5. PRODUCT TABLE -->
              <div class="row row-products">
                <table class="product-table">
                  <thead>
                    <tr>
                      <th style="width: 58%;">PRODUCT (QTY)</th>
                      <th style="width: 21%; text-align: center;">PRICE</th>
                      <th style="width: 21%; text-align: center; border-right: none;">TOTAL</th>
                    </tr>
                  </thead>

                  <tbody>
                    ${productsHtml}

                    <tr style="background: #ebebeb;">
                      <td style="border-top: 1.5px solid #000;">TOTAL</td>
                      <td colspan="2"
                          style="text-align: center; border-top: 1.5px solid #000; border-right: none; font-size: 10pt;">
                        INR ${order.amount}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- 6. ORDER ID ROW -->
              <div class="row row-orderid"
                   style="display:flex; align-items:center; justify-content:center;">
                <div class="order-id-text">
                  ORDER ID ${order.order_id_seq || order.order_id}
                </div>
              </div>

              <!-- 7. FOOTER ROW -->
              <div class="row row-footer"
                   style="display:flex; align-items:center; justify-content:center; border-bottom: none;">
                <div class="footer-text">
                  THANK YOU FOR SHOPPING
                </div>
              </div>
            </div>
          </div>
        `;
      } else {
        return `
          <div class="label-page prepaid">
            <div class="label-wrapper">
              <div class="row row-header">
                <div class="header-full">
                  <div class="store-name">${storeName}</div>
                </div>
              </div>

              <div class="row row-pin">
                <div class="pin-left">PINCODE: ${order.address?.zip || ""}</div>
                <div class="type-right">PREPAID / SPEEDPOST</div>
              </div>

              <div class="row row-shipto">
                <div class="shipto-cell">
                  <div>
                    <div style="font-size: 9pt; font-weight: 900; margin-bottom: 2px;">SHIP TO:</div>
                    <div style="font-size: 12pt; font-weight: 900;">${order.address?.name || "Customer"}</div>
                    <div class="shipto-addr-heavy">
                      ${order.address?.address || ""}<br>
                      ${order.address?.place ? order.address.place + "<br>" : ""}
                      ${postofficeName ? "Postoffice Name: " + postofficeName + "<br>" : ""}
                      ${order.address?.city || ""}, ${order.address?.state || ""}<br>
                      PIN: ${order.address?.zip || ""}
                    </div>
                  </div>
                  <div style="font-size: 13pt; font-weight: 900; margin-top: 4px; line-height: 1.2;">
                    PH: ${order.address?.phone || order.phone || ""}
                  </div>
                </div>

                <div class="prepaid-cell">
                  <div class="ebiller-box-inner">
                    <div style="font-size: 8pt; font-weight: 900;">E-BILLER ID</div>
                    <div class="ebiller-id">55938</div>
                  </div>

                  <div class="paid-text-ultra">PAID</div>
                  <div class="order-subtext">ORDER</div>
                </div>
              </div>

              <div class="row row-seller-date">
                <div class="seller-cell">
                  <div style="font-size: 8pt; font-weight: 700; margin-bottom: 2px;">SELLER:</div>
                  <div class="shipto-name" style="font-size: 9pt;">${storeName}</div>
                  <div class="seller-addr-bold">
                    PH: 7012556844<br>
                    KANJIRAMATTOM, KERALA<br>
                    INDIA 682315
                  </div>
                </div>

                <div style="width: 35%; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center;">
                  <div style="font-size: 8pt; font-weight: 700;">ORDER DATE</div>
                  <div style="font-size: 9pt; font-weight: 900;">
                    ${dateStr}<br>
                    ${timeStr}
                  </div>
                </div>
              </div>

              <div class="row row-products">
                <table class="product-table">
                  <thead>
                    <tr>
                      <th style="width: 70%;">PRODUCT (QTY)</th>
                      <th style="width: 30%; text-align: center; border-right: none;">TOTAL</th>
                    </tr>
                  </thead>

                  <tbody>
                    ${productsHtml}

                    <tr style="background: #ebebeb;">
                      <td style="border-top: 1.5px solid #000;">TOTAL</td>
                      <td style="text-align: center; border-top: 1.5px solid #000; border-right: none; font-size: 10pt;">
                        INR ${order.amount}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="row row-orderid">
                <div class="order-id-box">
                  <div class="order-id-text">
                    ORDER ID ${order.order_id_seq || order.order_id}
                  </div>
                </div>
              </div>

              <div class="row row-footer"
                   style="display:flex; align-items:center; justify-content:center; border-bottom: none;">
                <div class="footer-text">
                  THANK YOU FOR SHOPPING
                </div>
              </div>
            </div>
          </div>
        `;
      }
    }).join("");

    const fullDocument = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Taqwa Invoices (${ordersToPrint.length})</title>
<link href="https://fonts.googleapis.com/css2?family=Roboto+Condensed:wght@400;700;900&family=Roboto:wght@400;700;900&display=swap" rel="stylesheet">
<style>
  @page {
    size: 4in 6in;
    margin: 0.1in;
  }
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
  }
  body {
    font-family: 'Roboto Condensed', 'Roboto', sans-serif;
    font-size: 10pt;
    color: #000;
    text-transform: uppercase;
  }
  .label-page {
    width: 3.8in;
    height: 5.8in;
    page-break-after: always;
    break-after: page;
    page-break-inside: avoid;
    break-inside: avoid;
    box-sizing: border-box;
    margin: 0 auto;
    overflow: hidden;
  }
  .label-page:last-child {
    page-break-after: auto;
    break-after: auto;
  }
  .label-wrapper {
    width: 100%;
    height: 100%;
    border: 2px solid #000;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }
  .row {
    display: flex;
    width: 100%;
    border-bottom: 1.5px solid #000;
    overflow: hidden;
  }
  .row:last-child {
    border-bottom: none;
  }
  .row-header      { flex: 1.0; }
  .row-pin         { flex: 0.5; }
  .row-shipto      { flex: 5.2; }
  .row-seller-date { flex: 1.8; } 
  .row-products    { flex: 3.2; display: block; }
  .row-orderid     { flex: 0.6; }
  .row-footer      { flex: 0.5; }

  .label-page.prepaid .row-shipto { flex: 5.4; }
  .label-page.prepaid .row-products { flex: 2.7; display: block; }
  .label-page.prepaid .row-footer { flex: 0.6; }

  .store-name {
    font-size: 17pt;
    font-weight: 900;
    text-align: center;
    line-height: 1.2;
    width: 100%;
  }
  .header-full {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 8px;
  }
  .pin-left {
    width: 50%;
    padding: 4px 8px;
    font-size: 10pt;
    font-weight: 900;
    border-right: 1.5px solid #000;
    display: flex;
    align-items: center;
  }
  .route-right, .type-right {
    width: 50%;
    padding: 4px 8px;
    font-size: 10pt;
    font-weight: 900;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
  .shipto-cell {
    padding: 4px 8px;
    border-right: 1.5px solid #000;
    display: flex;
    flex-direction: column;
  }
  .label-page.cod .shipto-cell { width: 62%; }
  .label-page.prepaid .shipto-cell { width: 72%; }

  .shipto-addr-heavy {
    font-size: 10.5pt;
    font-weight: 900;
    line-height: 1.3;
    margin-top: 2px;
    word-break: break-word;
    overflow-wrap: break-word;
  }
  .cod-cell {
    width: 38%;
    padding: 7px 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    background: #fff;
  }
  .cod-amount-title {
    font-weight: 900;
    font-size: 10pt; 
    line-height: 1.1;
    margin-bottom: 4px;
    white-space: nowrap;
  }
  .shipto-name {
    font-size: 12pt;
    font-weight: 900;
  }
  .prepaid-cell {
    width: 28%;
    padding: 5px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    background: #f2f2f2;
  }
  .ebiller-box-inner {
    border: 1.5px solid #000;
    padding: 5px 2px;
    width: 100%;
  }
  .ebiller-id {
    font-size: 13pt; 
    font-weight: 900;
    letter-spacing: 1px;
    line-height: 1.1;
  }
  .paid-text-ultra {
    font-weight: 900;
    font-size: 28pt; 
    letter-spacing: 2px;
    line-height: 1;
    margin-top: 8px;
  }
  .order-subtext {
    font-weight: 400;
    font-size: 9.5pt;
    letter-spacing: 1.5px;
    margin-top: 4px;
  }
  .seller-cell {
    width: 65%;
    padding: 7px 9px;
    border-right: 1.5px solid #000;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .seller-addr-bold {
    font-size: 8.5pt;
    font-weight: 700; 
    line-height: 1.4;
  }
  .label-page.prepaid .seller-addr-bold {
    font-size: 9pt;
    font-weight: 900;
  }
  .product-table {
    width: 100%;
    border-collapse: collapse;
  }
  .product-table th, .product-table td {
    padding: 5px 7px;
    border-right: 1.5px solid #000;
    font-weight: 900;
  }
  .product-table th {
    font-size: 8pt;
    border-bottom: 1.5px solid #000;
    background: #ebebeb;
    text-align: left;
  }
  .product-table td {
    font-size: 9pt;
    border-bottom: 1px solid #ccc;
  }
  .order-id-box {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    padding: 4px;
  }
  .order-id-text {
    font-size: 12pt;
    font-weight: 900;
    letter-spacing: 1px;
    text-align: center;
  }
  .footer-text {
    font-size: 14pt;
    font-weight: 900;
    letter-spacing: 1px;
    text-align: center;
    width: 100%;
  }
  .label-page.prepaid .footer-text {
    font-size: 16pt;
    line-height: 1;
  }
</style>
</head>
<body>
${pagesHtml}
</body>
</html>`;

    printWindow.document.write(fullDocument);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handleBulkPrintInvoices = async () => {
    const selectedOrders = sortedOrders.filter((o) => selectedOrderIds.includes(o.order_id));
    if (selectedOrders.length === 0) {
      toast.warning("Please select at least one order to print invoices.");
      return;
    }
    await printInvoices(selectedOrders);
  };

  const handlePrintSingleInvoice = async (order) => {
    await printInvoices([order]);
  };

  // Helper to extract clean tracking ID string from order
  const getOrderTrackingId = (order) => {
    if (!order || !order.tracking_id) return "";
    if (typeof order.tracking_id === "string") {
      try {
        const parsed = JSON.parse(order.tracking_id);
        if (typeof parsed === "object" && parsed !== null) {
          const uniqueVals = [...new Set(Object.values(parsed).filter((v) => v && String(v).trim() !== ""))];
          return uniqueVals.join(", ");
        }
        return order.tracking_id;
      } catch (e) {
        return order.tracking_id;
      }
    }
    if (typeof order.tracking_id === "object") {
      const uniqueVals = [...new Set(Object.values(order.tracking_id).filter((v) => v && String(v).trim() !== ""))];
      return uniqueVals.join(", ");
    }
    return String(order.tracking_id);
  };

  const isTrackingIdChanged = (order) => {
    const typedVal = orderTrackingInputs[order.order_id];
    if (typedVal === undefined) return false;
    const currentDbVal = getOrderTrackingId(order);
    return typedVal.trim() !== currentDbVal.trim();
  };

  // Save Tracking ID & Send via WhatsApp
  const handleSaveAndWhatsApp = async (order) => {
    if (!order || !order.order_id) return;

    const currentTrackingId =
      orderTrackingInputs[order.order_id] !== undefined
        ? orderTrackingInputs[order.order_id]
        : getOrderTrackingId(order);

    if (!currentTrackingId || !String(currentTrackingId).trim()) {
      toast.warning("Please enter a Tracking ID before saving and sending to WhatsApp!");
      return;
    }

    const trimmedTrackingId = String(currentTrackingId).trim();
    const wpNumber = order.address?.whatsapp || order.address?.phone || order.whatsapp || order.phone;
    if (!wpNumber) {
      toast.error("No phone or WhatsApp number found for this order!");
      return;
    }

    setIsSendingWhatsApp(order.order_id);

    try {
      const itemsArray = Array.isArray(order.items) ? order.items : Object.values(order.items || {});
      let currentTracking = {};
      if (typeof order.tracking_id === "object" && order.tracking_id !== null) {
        currentTracking = { ...order.tracking_id };
      } else if (typeof order.tracking_id === "string" && order.tracking_id.trim() !== "") {
        try {
          currentTracking = JSON.parse(order.tracking_id);
          if (typeof currentTracking !== "object" || currentTracking === null) {
            currentTracking = {};
          }
        } catch (e) {
          currentTracking = { legacy: order.tracking_id };
        }
      }

      const updatedTracking = { ...currentTracking };
      if (itemsArray.length > 0) {
        itemsArray.forEach((item) => {
          const itemKey = `${item.id}_${item.size || "default"}`;
          updatedTracking[itemKey] = trimmedTrackingId;
        });
      }
      updatedTracking.default = trimmedTrackingId;

      const updatePayload = {
        tracking_id: JSON.stringify(updatedTracking),
      };

      if (order.order_status === "waiting") {
        updatePayload.order_status = "shipped";
        updatePayload.hold_status = null;
      }

      const { error: dbError } = await supabase
        .from("orders")
        .update(updatePayload)
        .eq("order_id", order.order_id);

      if (dbError) {
        console.error("Error saving tracking ID:", dbError);
        toast.error(`Error saving tracking ID: ${dbError.message || "Database error"}`);
        setIsSendingWhatsApp(null);
        return;
      }

      // Update local state
      setOrders((prev) =>
        prev.map((o) => {
          if (o.order_id === order.order_id) {
            return {
              ...o,
              tracking_id: updatedTracking,
              order_status: order.order_status === "waiting" ? "shipped" : o.order_status,
              hold_status: order.order_status === "waiting" ? null : o.hold_status,
            };
          }
          return o;
        })
      );

      // Clean up edited input state so it syncs with updated order
      setOrderTrackingInputs((prev) => {
        const next = { ...prev };
        delete next[order.order_id];
        return next;
      });

      // Send WhatsApp notification
      const isPerfumes =
        (order.template_type && order.template_type.toLowerCase().includes("perfume")) ||
        (order.store_name && order.store_name.toLowerCase().includes("perfume")) ||
        itemsArray.some(
          (item) =>
            (item?.name && item.name.toLowerCase().includes("perfume")) ||
            (item?.category && item.category.toLowerCase().includes("perfume"))
        );

      const templateName = isPerfumes ? "taqwa_perfumes" : "taqwa_fashion_store";
      const customerName = order.address?.name || order.name || "Customer";

      const res = await sendWhatsAppNotification({
        to: wpNumber,
        customerName,
        trackingId: trimmedTrackingId,
        templateName,
      });

      if (res.success) {
        toast.success("Tracking ID saved & WhatsApp notification sent!");
        fetchAllOrdersForKPIs();
      }
    } catch (err) {
      console.error("Error saving tracking ID & sending WhatsApp:", err);
      toast.error("Failed to save tracking ID and send WhatsApp");
    } finally {
      setIsSendingWhatsApp(null);
    }
  };

  // Save Tracking ID to DB only (without WhatsApp)
  const handleSaveTrackingOnly = async (order) => {
    if (!order || !order.order_id) return;

    const currentTrackingId =
      orderTrackingInputs[order.order_id] !== undefined
        ? orderTrackingInputs[order.order_id]
        : getOrderTrackingId(order);

    const trimmedTrackingId = String(currentTrackingId || "").trim();
    if (!trimmedTrackingId) {
      toast.warning("Please enter a Tracking ID to save!");
      return;
    }

    setIsSavingTracking(order.order_id);

    try {
      const itemsArray = Array.isArray(order.items) ? order.items : Object.values(order.items || {});
      let currentTracking = {};
      if (typeof order.tracking_id === "object" && order.tracking_id !== null) {
        currentTracking = { ...order.tracking_id };
      } else if (typeof order.tracking_id === "string" && order.tracking_id.trim() !== "") {
        try {
          currentTracking = JSON.parse(order.tracking_id);
          if (typeof currentTracking !== "object" || currentTracking === null) {
            currentTracking = {};
          }
        } catch (e) {
          currentTracking = { legacy: order.tracking_id };
        }
      }

      const updatedTracking = { ...currentTracking };
      if (itemsArray.length > 0) {
        itemsArray.forEach((item) => {
          const itemKey = `${item.id}_${item.size || "default"}`;
          updatedTracking[itemKey] = trimmedTrackingId;
        });
      }
      updatedTracking.default = trimmedTrackingId;

      const { error: dbError } = await supabase
        .from("orders")
        .update({ tracking_id: JSON.stringify(updatedTracking) })
        .eq("order_id", order.order_id);

      if (dbError) {
        console.error("Error saving tracking ID:", dbError);
        toast.error(`Error saving tracking ID: ${dbError.message || "Database error"}`);
      } else {
        toast.success("Tracking ID updated successfully!");
        setOrders((prev) =>
          prev.map((o) =>
            o.order_id === order.order_id ? { ...o, tracking_id: updatedTracking } : o
          )
        );
        setOrderTrackingInputs((prev) => {
          const next = { ...prev };
          delete next[order.order_id];
          return next;
        });
      }
    } catch (err) {
      console.error("Error saving tracking ID:", err);
      toast.error("Failed to save tracking ID");
    } finally {
      setIsSavingTracking(null);
    }
  };

  const exportToCSV = () => {
    if (filteredOrders.length === 0) {
      toast.warning("No orders to export!");
      return;
    }
    const headers = ["Order ID", "Date", "Customer Name", "Phone", "Amount", "Payment Method", "Status", "Waybill", "Tracking ID"];
    const rows = filteredOrders.map((o) => [
      o.order_id_seq || o.order_id,
      moment(o.created_at).format("YYYY-MM-DD HH:mm"),
      o.address?.name || "N/A",
      o.address?.phone || o.phone || "N/A",
      o.amount,
      o.payment_method || "ONLINE",
      o.order_status,
      o.waybill || "",
      getOrderTrackingId(o) || ""
    ]);

    const csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(","), ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))].join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `orders_export_${moment().format("YYYYMMDD_HHmm")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Orders exported successfully as CSV!");
  };

  const handleCopyWaybill = (e, waybill) => {
    e.stopPropagation();
    navigator.clipboard.writeText(waybill);
    setCopiedOrderId(waybill);
    toast.success("Waybill copied to clipboard!");
    setTimeout(() => setCopiedOrderId(null), 2000);
  };


  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedOrderIds(sortedOrders.map(o => o.order_id));
    } else {
      setSelectedOrderIds([]);
    }
  };

  const handleSelectOne = (e, orderId) => {
    e.stopPropagation();
    if (selectedOrderIds.includes(orderId)) {
      setSelectedOrderIds(selectedOrderIds.filter(id => id !== orderId));
    } else {
      setSelectedOrderIds([...selectedOrderIds, orderId]);
    }
  };

  // Sparkline Chart generator
  const drawSparkline = (dataPoints, color) => {
    if (!dataPoints || dataPoints.length < 2) {
      return (
        <svg className="w-14 h-5" viewBox="0 0 100 20">
          <line x1="0" y1="10" x2="100" y2="10" stroke={color} strokeWidth="1.5" strokeDasharray="2" />
        </svg>
      );
    }
    const maxVal = Math.max(...dataPoints);
    const minVal = Math.min(...dataPoints);
    const range = maxVal - minVal || 1;
    const points = dataPoints
      .map((val, index) => {
        const x = (index / (dataPoints.length - 1)) * 90 + 5;
        const y = 18 - ((val - minVal) / range) * 16;
        return `${x},${y}`;
      })
      .join(" ");

    return (
      <svg className="w-16 h-5" viewBox="0 0 100 20">
        <polyline fill="none" stroke={color} strokeWidth="2" points={points} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  };

  // ---- KPI METRICS: computed from allOrdersForKPIs (all orders, no tab filter) ----

  // Current period filter
  const currentPeriodOrders = allOrdersForKPIs.filter((o) => {
    const orderDate = moment(o.created_at);
    if (timeframeDays === 1) {
      return orderDate.isSameOrAfter(moment().startOf("day"));
    }
    if (timeframeDays === "yesterday") {
      const yesterdayStart = moment().subtract(1, "days").startOf("day");
      const yesterdayEnd = moment().startOf("day");
      return orderDate.isSameOrAfter(yesterdayStart) && orderDate.isBefore(yesterdayEnd);
    }
    return orderDate.isSameOrAfter(moment().subtract(timeframeDays, "days").startOf("day"));
  });

  // Previous period filter (same length window, shifted back)
  const prevPeriodOrders = allOrdersForKPIs.filter((o) => {
    const orderDate = moment(o.created_at);
    if (timeframeDays === 1) {
      const prevStart = moment().subtract(1, "days").startOf("day");
      const prevEnd = moment().startOf("day");
      return orderDate.isSameOrAfter(prevStart) && orderDate.isBefore(prevEnd);
    }
    if (timeframeDays === "yesterday") {
      const prevStart = moment().subtract(2, "days").startOf("day");
      const prevEnd = moment().subtract(1, "days").startOf("day");
      return orderDate.isSameOrAfter(prevStart) && orderDate.isBefore(prevEnd);
    }
    const prevStart = moment().subtract(timeframeDays * 2, "days").startOf("day");
    const prevEnd = moment().subtract(timeframeDays, "days").startOf("day");
    return orderDate.isSameOrAfter(prevStart) && orderDate.isBefore(prevEnd);
  });

  // Helper: compute % change between two numbers
  const pctChange = (current, previous) => {
    if (previous === 0 && current === 0) return { value: 0, label: "—", color: "text-gray-400" };
    if (previous === 0) return { value: 100, label: "↗ 100%", color: "text-green-500" };
    const pct = Math.round(((current - previous) / previous) * 100);
    if (pct > 0) return { value: pct, label: `↗ ${pct}%`, color: "text-green-500" };
    if (pct < 0) return { value: pct, label: `↘ ${Math.abs(pct)}%`, color: "text-red-500" };
    return { value: 0, label: "—", color: "text-gray-400" };
  };

  // Helper: count items in an order
  const countItems = (o) => {
    const items = Array.isArray(o.items) ? o.items : Object.values(o.items || {});
    return items.reduce((subSum, item) => subSum + (Number(item.quantity) || 1), 0);
  };

  // Compute Trends over adjustable timeframe (using allOrdersForKPIs)
  const getDailyTrendData = () => {
    // Limit daily sparkline points to a maximum of 90 days for client-side performance
    const cappedDays = timeframeDays === "yesterday" ? 2 : Math.min(Number(timeframeDays), 90);
    const days = Math.max(cappedDays - 1, 1);
    const orderTrend = {};
    const itemsTrend = {};
    const revTrend = {};
    const fulfilledTrend = {};
    const pendingTrend = {};

    for (let i = days; i >= 0; i--) {
      const dateStr = moment().subtract(i, "days").format("YYYY-MM-DD");
      orderTrend[dateStr] = 0;
      itemsTrend[dateStr] = 0;
      revTrend[dateStr] = 0;
      fulfilledTrend[dateStr] = 0;
      pendingTrend[dateStr] = 0;
    }

    allOrdersForKPIs.forEach((o) => {
      const dateStr = moment(o.created_at).format("YYYY-MM-DD");
      if (orderTrend[dateStr] !== undefined) {
        orderTrend[dateStr]++;
        itemsTrend[dateStr] += countItems(o);
        revTrend[dateStr] += Number(o.amount) || 0;
        if (o.order_status === "shipped" || o.order_status === "delivered") {
          fulfilledTrend[dateStr]++;
        }
        if (o.order_status === "waiting") {
          pendingTrend[dateStr]++;
        }
      }
    });

    return {
      orders: Object.values(orderTrend),
      items: Object.values(itemsTrend),
      revenue: Object.values(revTrend),
      fulfilled: Object.values(fulfilledTrend),
      pending: Object.values(pendingTrend),
    };
  };

  const trends = getDailyTrendData();

  // Current period KPIs
  const kpis = {
    totalOrders: currentPeriodOrders.length,
    itemsOrdered: currentPeriodOrders.reduce((sum, o) => sum + countItems(o), 0),
    totalRevenue: currentPeriodOrders.reduce((sum, o) => sum + (Number(o.amount) || 0), 0),
    fulfilledOrders: currentPeriodOrders.filter(o => o.order_status === "shipped" || o.order_status === "delivered").length,
    deliveredOrders: currentPeriodOrders.filter(o => o.order_status === "delivered").length,
    pendingFulfillment: currentPeriodOrders.filter(o => o.order_status === "waiting").length,
  };

  // Previous period KPIs for comparison
  const prevKpis = {
    totalOrders: prevPeriodOrders.length,
    itemsOrdered: prevPeriodOrders.reduce((sum, o) => sum + countItems(o), 0),
    fulfilledOrders: prevPeriodOrders.filter(o => o.order_status === "shipped" || o.order_status === "delivered").length,
    deliveredOrders: prevPeriodOrders.filter(o => o.order_status === "delivered").length,
  };

  // Percentage changes
  const ordersPctChange = pctChange(kpis.totalOrders, prevKpis.totalOrders);
  const itemsPctChange = pctChange(kpis.itemsOrdered, prevKpis.itemsOrdered);
  const fulfilledPctChange = pctChange(kpis.fulfilledOrders, prevKpis.fulfilledOrders);
  const deliveredPctChange = pctChange(kpis.deliveredOrders, prevKpis.deliveredOrders);

  // Returns (cancelled orders value)
  const cancelledOrdersInTimeframe = currentPeriodOrders.filter(o => o.order_status === "cancelled_by_admin" || o.order_status === "cancelled" || o.order_status === "cancelled_keep_stock");
  const returnsValue = cancelledOrdersInTimeframe.reduce((sum, o) => sum + (Number(o.amount) || 0), 0);

  // Average fulfillment time: time from order creation to delivered/shipped
  const avgFulfillmentTime = (() => {
    const fulfilledInPeriod = currentPeriodOrders.filter(o => 
      o.order_status === "delivered" || o.order_status === "shipped"
    );
    if (fulfilledInPeriod.length === 0) return "N/A";
    // Estimate: use delivered_at if available, otherwise use rough estimate
    const totalHours = fulfilledInPeriod.reduce((sum, o) => {
      const created = moment(o.created_at);
      const fulfilled = o.delivered_at ? moment(o.delivered_at) : moment(); // approximate
      return sum + fulfilled.diff(created, "hours");
    }, 0);
    const avgHours = Math.round(totalHours / fulfilledInPeriod.length);
    if (avgHours < 1) return "< 1 hour";
    if (avgHours < 24) return `${avgHours} hours`;
    const days = Math.round(avgHours / 24);
    return `${days} day${days > 1 ? "s" : ""}`;
  })();

  const getOrderStatusPills = (order, isBlocked) => {
    // Payment status pill
    let payPillStyle = "bg-[#fed7aa] text-[#9a3412] border-[#fdba74]";
    let payText = "Payment pending";
    if (order.payment_method === "COD") {
      payPillStyle = "bg-[#fed7aa] text-[#9a3412] border-[#fdba74]";
      payText = "Payment pending";
    } else if (order.payment_method) {
      payPillStyle = "bg-[#c7f0db] text-[#006039] border-[#a6e6c7]";
      payText = "Paid";
    }

    // Fulfillment status pill
    let fulfillPillStyle = "bg-[#feedaa] text-[#593d00] border-[#f5dc82]";
    let fulfillText = "Unfulfilled";
    if (isBlocked) {
      fulfillPillStyle = "bg-[#fff0f0] text-[#cc0000] border-[#ffcccc] font-semibold";
      fulfillText = "Cancelled & Blocked";
    } else if (order.order_status === "shipped") {
      fulfillPillStyle = "bg-[#e2f0fd] text-[#0066cc] border-[#cce6ff]";
      fulfillText = "Shipped";
    } else if (order.order_status === "delivered") {
      fulfillPillStyle = "bg-[#c7f0db] text-[#006039] border-[#a6e6c7]";
      fulfillText = "Fulfilled";
    } else if (
      order.order_status === "cancelled_by_admin" ||
      order.order_status === "cancelled" ||
      order.order_status === "cancelled_keep_stock" ||
      order.order_status === "cancelled_blocked"
    ) {
      fulfillPillStyle = "bg-[#fff0f0] text-[#cc0000] border-[#ffcccc] font-semibold";
      fulfillText =
        order.order_status === "cancelled_keep_stock"
          ? "Cancelled (Keep Stock)"
          : order.order_status === "cancelled_blocked"
          ? "Cancelled & Blocked"
          : "Cancelled & Released Stock";
    } else if (order.order_status === "ready_to_pickup" || order.waybill) {
      fulfillPillStyle = "bg-[#f3e8ff] text-[#7c3aed] border-[#edd8ff]";
      fulfillText = "Ready to Pickup";
    }

    return { payPillStyle, payText, fulfillPillStyle, fulfillText };
  };

  const formatOrderSubtitle = (order) => {
    const customerName = order.address?.name || "Customer";
    const itemsArray = Array.isArray(order.items) ? order.items : Object.values(order.items || {});
    const totalItems = itemsArray.reduce((acc, i) => acc + (Number(i?.quantity) || 1), 0);
    const itemsText = `${totalItems} ${totalItems === 1 ? "item" : "items"}`;

    const createdMoment = moment(order.created_at);
    let timeStr = "";
    if (createdMoment.isSame(moment(), "day")) {
      timeStr = createdMoment.format("hh:mm A");
    } else if (createdMoment.isSame(moment().subtract(1, "day"), "day")) {
      timeStr = `Yesterday, ${createdMoment.format("hh:mm A")}`;
    } else {
      timeStr = createdMoment.format("DD MMM, hh:mm A");
    }

    const phone = order.address?.phone || order.phone;
    const productsSummary = itemsArray
      .map((i) => `${i?.name || "Product"}${i?.size ? ` (${i.size})` : ""}${i?.quantity && i.quantity > 1 ? ` x${i.quantity}` : ""}`)
      .join(", ");

    return { customerName, totalItems, itemsText, timeStr, phone, productsSummary };
  };

  const getPaymentMethodDisplay = (order) => {
    if (order.payment_method === "COD") return "Cash On Delivery";
    if (order.payment_method === "Razorpay") return "Razorpay (Online)";
    if (order.payment_method) return order.payment_method;
    return "Prepaid";
  };

  const getDayHeaderLabel = (order, idx) => {
    if (sortColumn !== "date" || sortDirection !== "desc") return null;
    const isNewDay = idx === 0 || !moment(order.created_at).isSame(sortedOrders[idx - 1].created_at, "day");
    if (!isNewDay) return null;
    const m = moment(order.created_at);
    if (m.isSame(moment(), "day")) return "Today";
    if (m.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
    return m.format("DD MMMM YYYY");
  };

  const tabs = [
    { id: "all", label: "All" },
    { id: "waiting", label: "Unfulfilled" },
    { id: "ready_to_pickup", label: "Ready to Pickup" },
    { id: "shipped", label: "Shipped" },
    { id: "delivered", label: "Delivered" },
    { id: "cancelled", label: "Cancelled" }
  ];

  return (
    <div className="page min-h-screen bg-[#f6f6f7] text-[#202223] font-sans pb-20">
      <TopBar avatarInfo={session?.user?.user_metadata} />

      {/* Compact Shopify-style Header */}
      <div className="px-3 sm:px-6 py-4 max-w-[1850px] mx-auto flex flex-wrap gap-3 items-center justify-between bg-white border border-gray-200 rounded-lg shadow-sm mt-4 mb-4">
        <h1 className="text-lg font-bold tracking-tight text-gray-900 flex items-center gap-2">
          <LuShoppingBag className="text-gray-700" size={20} />
          Orders
        </h1>

        <div className="flex items-center gap-2 relative">
          <button
            onClick={exportToCSV}
            className="px-3.5 py-1.5 bg-[#f1f2f4] hover:bg-gray-200 text-xs font-semibold rounded text-gray-700 transition-colors shadow-sm"
          >
            Export
          </button>
          
          <div className="relative">
            <button
              onClick={() => setMoreActionsOpen(!moreActionsOpen)}
              className="px-3.5 py-1.5 bg-[#f1f2f4] hover:bg-gray-200 text-xs font-semibold rounded text-gray-700 transition-colors flex items-center gap-1 shadow-sm"
            >
              More actions
              <FiChevronDown size={12} />
            </button>
            {moreActionsOpen && (
              <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-50 py-1 divide-y divide-gray-100">
                <button
                  onClick={() => {
                    fetchOrders();
                    setMoreActionsOpen(false);
                    toast.success("Order metrics refreshed!");
                  }}
                  className="w-full text-left px-4 py-2.5 text-xs text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Refresh Data
                </button>
                <button
                  onClick={() => {
                    setShowStatsGrid(!showStatsGrid);
                    setMoreActionsOpen(false);
                  }}
                  className="w-full text-left px-4 py-2.5 text-xs text-gray-700 hover:bg-gray-100 transition-colors flex items-center justify-between font-semibold"
                >
                  <span>{showStatsGrid ? "Hide Metrics Panel" : "Show Metrics Panel"}</span>
                  <FiGrid size={12} className="text-gray-400" />
                </button>
              </div>
            )}
          </div>

          <button
            onClick={() => navigate("/create-invoice")}
            className="px-3.5 py-1.5 bg-black hover:bg-gray-800 text-white text-xs font-semibold rounded transition-all shadow-sm"
          >
            Create order
          </button>
        </div>
      </div>

      {/* Premium Compact Unified Analytics Grid */}
      {showStatsGrid && (
        <div className="px-3 sm:px-6 max-w-[1850px] mx-auto mb-5 relative z-30">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col xl:flex-row divide-y xl:divide-y-0 xl:divide-x divide-gray-200 relative">
            
            {/* 1. Timeframe Column */}
            <div 
              ref={timeframeRef}
              className="py-1.5 px-3 bg-[#f9fafb] rounded-t-lg xl:rounded-tr-none xl:rounded-l-lg flex-shrink-0 flex items-center justify-between xl:justify-center gap-1.5 hover:bg-gray-50 transition-colors cursor-pointer relative min-w-[110px]" 
              onClick={() => setTimeframeOpen(!timeframeOpen)}
            >
              <div className="flex items-center gap-1 text-[11px] font-bold text-gray-700">
                <FiCalendar className="text-gray-400" size={12} />
                <span>{timeframeOptions.find(o => o.days === timeframeDays)?.label || "Last 7 days"}</span>
              </div>
              <FiChevronDown size={10} className={`text-gray-400 transition-transform ${timeframeOpen ? "rotate-180" : ""}`} />
              {timeframeOpen && (
                <div className="absolute top-full left-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-xl z-50 py-1">
                  {timeframeOptions.map((opt) => (
                    <button
                      key={opt.days}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTimeframeDays(opt.days);
                        setTimeframeOpen(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 transition-colors flex items-center justify-between ${timeframeDays === opt.days ? "font-bold text-black bg-gray-50" : "text-gray-600"}`}
                    >
                      <span>{opt.label}</span>
                      {timeframeDays === opt.days && <FiCheck size={12} className="text-blue-600" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* 2. Orders Column */}
            <div className="py-1.5 px-3 flex-1 flex items-center justify-between min-w-0">
              <div className="min-w-0">
                <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Orders</div>
                <div className="flex items-baseline gap-1 mt-0.5">
                  <span className="text-sm font-black text-gray-900">{kpis.totalOrders}</span>
                  <span className={`text-[9px] ${ordersPctChange.color} font-bold flex items-center`}>{ordersPctChange.label}</span>
                </div>
              </div>
              <div className="flex-shrink-0 ml-1.5">
                {drawSparkline(trends.orders, "#3b82f6")}
              </div>
            </div>

            {/* 3. Items Ordered Column */}
            <div className="py-1.5 px-3 flex-1 flex items-center justify-between min-w-0">
              <div className="min-w-0">
                <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Items ordered</div>
                <div className="flex items-baseline gap-1 mt-0.5">
                  <span className="text-sm font-black text-gray-900">{kpis.itemsOrdered}</span>
                  <span className={`text-[9px] ${itemsPctChange.color} font-bold flex items-center`}>{itemsPctChange.label}</span>
                </div>
              </div>
              <div className="flex-shrink-0 ml-1.5">
                {drawSparkline(trends.items, "#10b981")}
              </div>
            </div>

            {/* 4. Returns Column */}
            <div className="py-1.5 px-3 flex-1 flex items-center justify-between min-w-0">
              <div className="min-w-0">
                <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Returns</div>
                <div className="flex items-baseline gap-1 mt-0.5">
                  <span className="text-sm font-black text-gray-900">₹{returnsValue.toLocaleString()}</span>
                  <span className="text-[9px] text-gray-400 font-bold">—</span>
                </div>
              </div>
              <div className="flex-shrink-0 ml-1.5">
                {drawSparkline([1, 1, 1, 1], "#2563eb")}
              </div>
            </div>

            {/* 5. Orders Fulfilled Column */}
            <div className="py-1.5 px-3 flex-1 flex items-center justify-between min-w-0">
              <div className="min-w-0">
                <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Orders fulfilled</div>
                <div className="flex items-baseline gap-1 mt-0.5">
                  <span className="text-sm font-black text-gray-900">{kpis.fulfilledOrders}</span>
                  <span className={`text-[9px] ${fulfilledPctChange.color} font-bold flex items-center`}>{fulfilledPctChange.label}</span>
                </div>
              </div>
              <div className="flex-shrink-0 ml-1.5">
                {drawSparkline(trends.fulfilled, "#6366f1")}
              </div>
            </div>

            {/* 6. Orders Delivered Column */}
            <div className="py-1.5 px-3 flex-1 flex items-center justify-between min-w-0">
              <div className="min-w-0">
                <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Orders delivered</div>
                <div className="flex items-baseline gap-1 mt-0.5">
                  <span className="text-sm font-black text-gray-900">{kpis.deliveredOrders}</span>
                  <span className={`text-[9px] ${deliveredPctChange.color} font-bold flex items-center`}>{deliveredPctChange.label}</span>
                </div>
              </div>
              <div className="flex-shrink-0 ml-1.5">
                {drawSparkline([1, 1, 1, 1], "#2563eb")}
              </div>
            </div>

            {/* 7. Avg Fulfillment Column */}
            <div className="py-1.5 px-3 flex-1 flex items-center justify-between min-w-0">
              <div className="min-w-0">
                <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Avg. fulfillment</div>
                <div className="flex items-baseline gap-1 mt-0.5">
                  <span className="text-sm font-black text-gray-900">{avgFulfillmentTime}</span>
                  <span className="text-[9px] text-gray-400 font-bold">—</span>
                </div>
              </div>
              <div className="flex-shrink-0 ml-1.5">
                {drawSparkline(trends.pending, "#f59e0b")}
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Main Shopify-style Grid Table card container */}
      <div className="px-3 sm:px-6 max-w-[1850px] mx-auto">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          
          {/* sleek Shopify-style horizontal pill tabs */}
          <div className="flex border-b border-gray-200 overflow-x-auto scrollbar-none bg-[#fafafb] px-3 sm:px-4 py-2.5 gap-2">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => {
                  setStatus(t.id);
                  setPage(1);
                  setSelectedOrderIds([]);
                }}
                className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer ${
                  status === t.id
                    ? "bg-gray-900 text-white font-bold shadow-xs"
                    : "bg-white text-gray-600 hover:text-gray-900 hover:bg-gray-100 border border-gray-200"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Search, Filter basis, Sort and Bulk Actions bar */}
          <div className="p-3 sm:p-4 border-b border-gray-200 bg-white flex flex-col md:flex-row gap-3 items-center justify-between">
            
            {/* Search inputs & Sort Menu */}
            <div className="flex flex-col sm:flex-row gap-2 w-full md:max-w-3xl">
              <div className="relative">
                <select
                  value={searchBasis}
                  onChange={(e) => setSearchBasis(e.target.value)}
                  className="w-full sm:w-auto pl-3 pr-8 py-2 bg-white border border-gray-300 rounded text-sm text-gray-700 font-semibold focus:ring-1 focus:ring-black focus:outline-none appearance-none cursor-pointer shadow-3xs"
                >
                  <option value="name">Customer Name</option>
                  <option value="phone">Phone Number</option>
                  <option value="order_id">Order ID (Seq)</option>
                  <option value="pincode">Pincode</option>
                  <option value="whatsapp">WhatsApp Number</option>
                </select>
                <FiChevronDown className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>

              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder={`Search by ${searchBasis.replace("_", " ")}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-1 focus:ring-black focus:outline-none shadow-3xs"
                />
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>

              {/* Sort Dropdown Button (matching Shopify mobile/web design) */}
              <div className="relative shrink-0">
                <button
                  type="button"
                  onClick={() => setSortMenuOpen(!sortMenuOpen)}
                  className="w-full sm:w-auto px-3 py-2 bg-white border border-gray-300 rounded text-sm text-gray-700 font-semibold hover:bg-gray-50 flex items-center justify-center gap-1.5 shadow-3xs cursor-pointer"
                  title="Sort orders"
                >
                  <FiFilter size={14} className="text-gray-500" />
                  <span>Sort</span>
                  <FiChevronDown size={12} className="text-gray-400" />
                </button>
                {sortMenuOpen && (
                  <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-xl z-50 py-1 divide-y divide-gray-100 animate-in fade-in zoom-in-95 duration-100">
                    <div className="py-1">
                      {[
                        { label: "Date: Newest first", col: "date", dir: "desc" },
                        { label: "Date: Oldest first", col: "date", dir: "asc" },
                        { label: "Order ID: High to Low", col: "order", dir: "desc" },
                        { label: "Order ID: Low to High", col: "order", dir: "asc" },
                        { label: "Total: High to Low", col: "total", dir: "desc" },
                        { label: "Total: Low to High", col: "total", dir: "asc" },
                        { label: "Customer Name (A-Z)", col: "customer", dir: "asc" },
                        { label: "Customer Name (Z-A)", col: "customer", dir: "desc" },
                      ].map((opt) => (
                        <button
                          key={opt.label}
                          type="button"
                          onClick={() => {
                            setSortColumn(opt.col);
                            setSortDirection(opt.dir);
                            setSortMenuOpen(false);
                          }}
                          className={`w-full text-left px-3.5 py-1.5 text-xs hover:bg-gray-100 flex items-center justify-between transition-colors ${
                            sortColumn === opt.col && sortDirection === opt.dir ? "font-bold text-black bg-gray-50" : "text-gray-600"
                          }`}
                        >
                          <span>{opt.label}</span>
                          {sortColumn === opt.col && sortDirection === opt.dir && <FiCheck size={12} className="text-blue-600" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Bulk actions menu & Bulk Shipment button */}
            {selectedOrderIds.length > 0 && (
              <div className="relative w-full md:w-auto flex flex-wrap items-center justify-end gap-2.5">
                <button
                  onClick={handleBulkShipment}
                  disabled={isBulkCreatingShipment}
                  className={`w-full md:w-auto px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 shadow active:scale-95 ${
                    isBulkCreatingShipment ? "opacity-75 cursor-not-allowed" : "cursor-pointer"
                  }`}
                  title="Bulk create shipment for selected orders without opening individually"
                >
                  {isBulkCreatingShipment ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Creating Shipments...</span>
                    </>
                  ) : (
                    <>
                      <LiaShippingFastSolid size={18} />
                      <span>Bulk Shipment</span>
                    </>
                  )}
                </button>

                <button
                  onClick={handleBulkPrintInvoices}
                  className="w-full md:w-auto px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 shadow active:scale-95 cursor-pointer"
                  title="Bulk print Taqwa Invoices for selected orders"
                >
                  <TbPrinter size={18} />
                  <span>Bulk Print Invoices</span>
                </button>

                <div className="relative w-full md:w-auto">
                  <button
                    onClick={() => setBulkActionOpen(!bulkActionOpen)}
                    className="w-full md:w-auto px-4 py-2 bg-black text-white text-sm font-semibold rounded hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 shadow cursor-pointer"
                  >
                    Bulk Actions ({selectedOrderIds.length} selected)
                    <FiChevronDown size={14} />
                  </button>

                  {bulkActionOpen && (
                    <div className="absolute right-0 top-11 z-30 w-56 bg-white border border-gray-200 rounded-lg shadow-xl py-1 divide-y divide-gray-100 animate-in fade-in slide-in-from-top-1 duration-150">
                      <div className="py-1">
                        <button
                          onClick={handleBulkShipment}
                          disabled={isBulkCreatingShipment}
                          className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2 font-medium"
                        >
                          <LiaShippingFastSolid size={16} />
                          Create Bulk Shipments
                        </button>
                        <button
                          onClick={handleBulkPrintInvoices}
                          className="w-full text-left px-4 py-2 text-sm text-emerald-600 hover:bg-emerald-50 flex items-center gap-2 font-medium"
                        >
                          <TbPrinter size={16} />
                          Print Taqwa Invoices
                        </button>
                      </div>
                      <div className="py-1">
                        <button
                          onClick={() => handleBulkStatusUpdate("waiting")}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          Mark as Unfulfilled
                        </button>
                      <button
                        onClick={() => handleBulkStatusUpdate("ready_to_pickup")}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        Mark as Ready To Pickup
                      </button>
                      <button
                        onClick={() => handleBulkStatusUpdate("shipped")}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        Mark as Shipped
                      </button>
                      <button
                        onClick={() => handleBulkStatusUpdate("delivered")}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        Mark as Delivered
                      </button>
                      <button
                        onClick={() => handleBulkStatusUpdate("cancelled_by_admin")}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        Mark as Cancelled
                      </button>
                    </div>
                    <div className="py-1">
                      <button
                        onClick={handleBulkDelete}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 font-semibold"
                      >
                        <FiTrash2 size={14} />
                        Delete Selected
                      </button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            )}
          </div>

          {/* Mobile Native Shopify-Style Order List View */}
          <div className="block md:hidden">
            {/* Mobile Select All & Selection Status Bar */}
            {sortedOrders.length > 0 && (
              <div className="flex items-center justify-between px-4 py-2.5 bg-[#fbfbfc] border-b border-gray-200 text-xs font-semibold text-gray-600">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={sortedOrders.length > 0 && selectedOrderIds.length === sortedOrders.length}
                    className="rounded border-gray-300 text-black focus:ring-black w-4 h-4 cursor-pointer"
                  />
                  <span>Select All ({sortedOrders.length})</span>
                </label>
                {selectedOrderIds.length > 0 && (
                  <span className="text-blue-600 font-bold bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100">
                    {selectedOrderIds.length} selected
                  </span>
                )}
              </div>
            )}

            {sortedOrders.length === 0 ? (
              <div className="p-8 text-center text-gray-500 font-medium">
                <LuPackage size={36} className="text-gray-300 mx-auto mb-2" />
                <p>No orders found</p>
                <p className="text-xs text-gray-400 font-normal mt-0.5">Try changing your filter tabs or search query.</p>
              </div>
            ) : (
              sortedOrders.map((order, idx) => {
                const isSelected = selectedOrderIds.includes(order.order_id);
                const p1 = normalizePhone(order.phone);
                const p2 = normalizePhone(order.address?.phone);
                const isBlocked = (p1 && blockedPhones.has(p1)) || (p2 && blockedPhones.has(p2)) || order.order_status === "cancelled_blocked";
                const isCancelledByAdmin =
                  order.order_status === "cancelled_by_admin" ||
                  order.order_status === "cancelled" ||
                  order.order_status === "cancelled_keep_stock" ||
                  order.order_status === "cancelled_blocked";
                const shouldCrossOut = isCancelledByAdmin || isBlocked;

                const { payPillStyle, payText, fulfillPillStyle, fulfillText } = getOrderStatusPills(order, isBlocked);
                const { customerName, itemsText, timeStr, phone, productsSummary } = formatOrderSubtitle(order);
                const dayHeader = getDayHeaderLabel(order, idx);

                return (
                  <React.Fragment key={`m-order-wrap-${order.order_id}`}>
                    {dayHeader && (
                      <div className="bg-[#f6f6f7] px-4 py-1.5 text-xs font-bold text-gray-500 uppercase tracking-wider border-y border-gray-200">
                        {dayHeader}
                      </div>
                    )}
                    <div
                      onClick={() => navigate(`/order/${order.order_id}`)}
                      className={`p-4 border-b border-gray-200 transition-colors cursor-pointer ${
                        isSelected
                          ? "bg-blue-50/40"
                          : isBlocked
                          ? "bg-[#fff5f5]"
                          : "bg-white hover:bg-[#fafafb]"
                      }`}
                    >
                      {/* Line 1: Checkbox + Order # on left, Amount on right */}
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-2.5">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => handleSelectOne(e, order.order_id)}
                            onClick={(e) => e.stopPropagation()}
                            className="rounded border-gray-300 text-black focus:ring-black cursor-pointer w-4 h-4"
                          />
                          <span className={`text-[15px] font-bold tracking-tight ${shouldCrossOut ? "line-through text-gray-400" : "text-gray-900"}`}>
                            #{order.order_id_seq || order.order_id}
                          </span>
                        </div>
                        <span className={`text-[15px] font-bold ${shouldCrossOut ? "line-through text-gray-400" : "text-gray-900"}`}>
                          ₹{(Number(order.amount) || 0).toFixed(2)}
                        </span>
                      </div>

                      {/* Line 2: Customer • Items count • Time */}
                      <div className="text-xs text-gray-600 mb-2 flex items-center gap-1.5 flex-wrap">
                        <span className={`font-semibold ${shouldCrossOut ? "line-through text-gray-400" : "text-gray-800"}`}>
                          {customerName}
                        </span>
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-600 font-medium">{itemsText}</span>
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-500 font-medium">{timeStr}</span>
                        {phone && (
                          <>
                            <span className="text-gray-400">•</span>
                            <a
                              href={`tel:${phone}`}
                              onClick={(e) => e.stopPropagation()}
                              className="text-blue-600 hover:underline font-mono text-xs"
                              title="Tap to call customer"
                            >
                              {phone}
                            </a>
                          </>
                        )}
                      </div>

                      {/* Line 3: Status Pills (Shopify styling) */}
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <button
                          type="button"
                          onClick={(e) => cycleFulfillmentStatus(e, order)}
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold shadow-3xs cursor-pointer ${fulfillPillStyle}`}
                          title="Tap to cycle fulfillment status"
                        >
                          {fulfillText}
                        </button>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold shadow-3xs ${payPillStyle}`}>
                          {payText}
                        </span>
                        {order.order_status === "waiting" && order.hold_status && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border bg-amber-50 text-amber-700 border-amber-200">
                            {order.hold_status}
                          </span>
                        )}
                      </div>

                      {/* Line 4: Payment Method & Product summary */}
                      <div className="text-xs text-gray-500 mb-3 flex items-center gap-1.5 flex-wrap">
                        <span className="font-medium text-gray-700">{getPaymentMethodDisplay(order)}</span>
                        {productsSummary && (
                          <>
                            <span className="text-gray-300">•</span>
                            <span className="text-gray-500 truncate max-w-[280px] font-normal" title={productsSummary}>
                              {productsSummary}
                            </span>
                          </>
                        )}
                      </div>

                      {/* Mobile Practical Actions & Tracking Footer */}
                      <div
                        className="mt-2 pt-2.5 border-t border-gray-100 flex flex-col gap-2 bg-[#fcfcfd] -mx-4 -mb-4 p-3 rounded-b-lg"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          {/* Waybill info */}
                          {order.waybill ? (
                            <div className="flex items-center gap-1.5">
                              <span
                                onClick={(e) => handleCopyWaybill(e, order.waybill)}
                                className="inline-flex items-center gap-1 text-[11px] bg-white hover:bg-gray-100 text-gray-800 font-semibold px-2 py-1 rounded border border-gray-200 transition-colors cursor-pointer shadow-3xs"
                                title="Tap to copy waybill"
                              >
                                {copiedOrderId === order.waybill ? <FiCheck className="text-green-600" /> : <FiCopy />}
                                {order.waybill}
                              </span>
                              <button
                                onClick={() => navigate(`/trackawbs/${order.waybill}`)}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title="Track package"
                              >
                                <LuTrendingUp size={14} />
                              </button>
                            </div>
                          ) : (
                            <span className="text-[11px] text-gray-400 italic">No waybill</span>
                          )}

                          {/* Quick Action buttons */}
                          <div className="flex items-center gap-1.5 ml-auto">
                            <button
                              onClick={() => setPreviewOrder(order)}
                              className="p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md border border-blue-100 transition-all shadow-3xs cursor-pointer"
                              title="Preview Items"
                            >
                              <FiEye size={15} />
                            </button>
                            <button
                              onClick={() => handlePrintSingleInvoice(order)}
                              className="p-1.5 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-md border border-emerald-100 transition-all shadow-3xs cursor-pointer"
                              title="Print Invoice"
                            >
                              <TbPrinter size={15} />
                            </button>
                            {!shouldCrossOut ? (
                              <button
                                onClick={() => setCancelModalOrder(order)}
                                className="p-1.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-md border border-red-100 transition-all shadow-3xs cursor-pointer"
                                title="Cancel Order"
                              >
                                <FiXCircle size={15} />
                              </button>
                            ) : (
                              <button
                                onClick={(e) => handleUndoCancel(e, order)}
                                className="p-1.5 text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-md border border-amber-100 transition-all shadow-3xs cursor-pointer"
                                title="Undo Cancel"
                              >
                                <FiRotateCcw size={15} />
                              </button>
                            )}
                            <button
                              onClick={(e) => handleDeleteOrder(e, order)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors cursor-pointer"
                              title="Delete Order"
                            >
                              <FiTrash2 size={14} />
                            </button>
                          </div>
                        </div>

                        {/* Tracking ID input & WhatsApp Send */}
                        <div className="flex items-center gap-2 mt-0.5">
                          <input
                            type="text"
                            placeholder="Tracking ID"
                            value={
                              orderTrackingInputs[order.order_id] !== undefined
                                ? orderTrackingInputs[order.order_id]
                                : getOrderTrackingId(order)
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              setOrderTrackingInputs((prev) => ({
                                ...prev,
                                [order.order_id]: val,
                              }));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveAndWhatsApp(order);
                            }}
                            className="flex-1 text-xs px-2.5 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-black bg-white font-mono shadow-inner min-w-0"
                          />
                          <button
                            onClick={() => handleSaveAndWhatsApp(order)}
                            disabled={isSendingWhatsApp === order.order_id}
                            className="px-2.5 py-1.5 bg-[#25D366] hover:bg-[#20ba5a] text-white rounded text-xs font-semibold flex items-center gap-1 shadow-xs transition-all active:scale-95 disabled:opacity-50 shrink-0 cursor-pointer"
                            title="Save & Send via WhatsApp"
                          >
                            {isSendingWhatsApp === order.order_id ? (
                              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <IoLogoWhatsapp className="text-sm shrink-0" />
                            )}
                            <span>Send</span>
                          </button>
                          {isTrackingIdChanged(order) && (
                            <button
                              onClick={() => handleSaveTrackingOnly(order)}
                              disabled={isSavingTracking === order.order_id}
                              className="p-1.5 bg-gray-800 hover:bg-black text-white rounded text-xs shadow-xs transition-all active:scale-95 shrink-0 cursor-pointer"
                              title="Save Tracking Only"
                            >
                              {isSavingTracking === order.order_id ? (
                                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <TbDeviceFloppy size={14} />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })
            )}
          </div>

          {/* Desktop Wide Shopify-Style Table with Increased Information Width */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[1350px]">
              <thead>
                <tr className="bg-[#fafafb] text-[#6d7175] text-[11px] font-bold uppercase tracking-wider border-b border-gray-200">
                  <th className="p-4 w-12 text-center">
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={sortedOrders.length > 0 && selectedOrderIds.length === sortedOrders.length}
                      className="rounded border-gray-300 text-black focus:ring-black cursor-pointer w-4 h-4"
                    />
                  </th>
                  <th 
                    className="p-4 min-w-[460px] cursor-pointer select-none hover:text-gray-900 transition-colors group"
                    onClick={() => handleSort("order")}
                  >
                    <div className="flex items-center gap-1.5">
                      <span>Order & Customer Information</span>
                      <span className={`transition-opacity ${sortColumn === "order" || sortColumn === "date" ? "opacity-100" : "opacity-0 group-hover:opacity-40"}`}>
                        {sortDirection === "asc" ? <FiArrowUp size={12} /> : <FiArrowDown size={12} />}
                      </span>
                    </div>
                  </th>
                  <th className="p-4 min-w-[420px]">
                    <span>Waybill / Tracking Details</span>
                  </th>
                  <th className="p-4 min-w-[180px] text-center">
                    <span>Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {sortedOrders.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="p-12 text-center text-gray-500 font-medium">
                      <div className="flex flex-col items-center gap-2">
                        <LuPackage size={40} className="text-gray-300" />
                        <span>No orders found</span>
                        <span className="text-xs text-gray-400 font-normal">Try changing your filter tabs or search query.</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  sortedOrders.map((order, idx) => {
                    const isSelected = selectedOrderIds.includes(order.order_id);
                    const p1 = normalizePhone(order.phone);
                    const p2 = normalizePhone(order.address?.phone);
                    const isBlocked = (p1 && blockedPhones.has(p1)) || (p2 && blockedPhones.has(p2)) || order.order_status === "cancelled_blocked";
                    const isCancelledByAdmin =
                      order.order_status === "cancelled_by_admin" ||
                      order.order_status === "cancelled" ||
                      order.order_status === "cancelled_keep_stock" ||
                      order.order_status === "cancelled_blocked";
                    const shouldCrossOut = isCancelledByAdmin || isBlocked;

                    const { payPillStyle, payText, fulfillPillStyle, fulfillText } = getOrderStatusPills(order, isBlocked);
                    const { customerName, itemsText, timeStr, phone, productsSummary } = formatOrderSubtitle(order);
                    const dayHeader = getDayHeaderLabel(order, idx);

                    return (
                      <React.Fragment key={order.order_id}>
                        {dayHeader && (
                          <tr className="bg-[#f6f6f7] border-y border-gray-200">
                            <td colSpan="4" className="px-6 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                              {dayHeader}
                            </td>
                          </tr>
                        )}
                        <tr 
                          onClick={() => navigate(`/order/${order.order_id}`)}
                          className={`transition-colors duration-100 text-sm cursor-pointer group ${
                            isSelected 
                              ? "bg-[#f4f6f8] hover:bg-[#f4f6f8]" 
                              : isBlocked 
                                ? "bg-gradient-to-r from-[#fff3f3] to-white hover:from-[#ffe8e8]" 
                                : "hover:bg-[#f9fafb]"
                          }`}
                        >
                          {/* Checkbox */}
                          <td className="p-4 w-12 text-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => handleSelectOne(e, order.order_id)}
                              className="rounded border-gray-300 text-black focus:ring-black cursor-pointer w-4 h-4"
                            />
                          </td>

                          {/* Order Details (Shopify Format) */}
                          <td className="p-4 min-w-[460px]">
                            <div className="flex flex-col gap-1.5 py-0.5">
                              {/* Line 1: Order # on left, Amount on right */}
                              <div className="flex items-center justify-between gap-4">
                                <span className={`text-[15px] font-bold tracking-tight ${shouldCrossOut ? "line-through text-gray-400" : "text-gray-900 group-hover:text-blue-600 transition-colors"}`}>
                                  #{order.order_id_seq || order.order_id}
                                </span>
                                <span className={`text-[15px] font-bold ${shouldCrossOut ? "line-through text-gray-400" : "text-gray-900"}`}>
                                  ₹{(Number(order.amount) || 0).toFixed(2)}
                                </span>
                              </div>

                              {/* Line 2: Customer • Items count • Time • Phone */}
                              <div className="text-xs text-gray-600 flex items-center gap-1.5 flex-wrap">
                                <span 
                                  className={`font-semibold select-text cursor-text ${shouldCrossOut ? "line-through text-gray-400" : "text-gray-800"}`}
                                  onClick={(e) => {
                                    if (window.getSelection && window.getSelection().toString().trim().length > 0) e.stopPropagation();
                                  }}
                                >
                                  {customerName}
                                </span>
                                <span className="text-gray-400">•</span>
                                <span className="text-gray-600 font-medium">{itemsText}</span>
                                <span className="text-gray-400">•</span>
                                <span className="text-gray-500 font-medium">{timeStr}</span>
                                {phone && (
                                  <>
                                    <span className="text-gray-400">•</span>
                                    <span 
                                      className="text-gray-600 font-mono text-[11px] bg-gray-50 px-1 py-0.5 rounded border border-gray-100 select-text cursor-text"
                                      onClick={(e) => {
                                        if (window.getSelection && window.getSelection().toString().trim().length > 0) e.stopPropagation();
                                      }}
                                    >
                                      {phone}
                                    </span>
                                  </>
                                )}
                              </div>

                              {/* Line 3: Status Pills (Shopify styling) */}
                              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                <button
                                  type="button"
                                  onClick={(e) => cycleFulfillmentStatus(e, order)}
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold shadow-3xs cursor-pointer transition-transform active:scale-95 ${fulfillPillStyle}`}
                                  title="Click to cycle fulfillment status"
                                >
                                  {fulfillText}
                                </button>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold shadow-3xs ${payPillStyle}`}>
                                  {payText}
                                </span>
                                {order.order_status === "waiting" && order.hold_status && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border bg-amber-50 text-amber-700 border-amber-200">
                                    {order.hold_status}
                                  </span>
                                )}
                              </div>

                              {/* Line 4: Payment Method & Product summary */}
                              <div className="text-xs text-gray-500 flex items-center gap-1.5 flex-wrap mt-0.5">
                                <span className="font-medium text-gray-700">{getPaymentMethodDisplay(order)}</span>
                                {productsSummary && (
                                  <>
                                    <span className="text-gray-300">•</span>
                                    <span className="text-gray-500 truncate max-w-lg font-normal" title={productsSummary}>
                                      {productsSummary}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Waybill / Tracking */}
                          <td className={`p-4 min-w-[420px] ${shouldCrossOut ? "line-through text-gray-400" : ""}`} onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-3">
                              {/* Courier Waybill Info */}
                              <div className="shrink-0 min-w-[120px]">
                                {order.waybill ? (
                                  <div className="flex items-center gap-1.5">
                                    <span 
                                      onClick={(e) => handleCopyWaybill(e, order.waybill)}
                                      className="inline-flex items-center gap-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold px-2 py-1 rounded border border-gray-200 transition-colors cursor-pointer shadow-3xs"
                                      title="Click to copy waybill"
                                    >
                                      {copiedOrderId === order.waybill ? <FiCheck className="text-green-600" /> : <FiCopy />}
                                      {order.waybill}
                                    </span>
                                    <button
                                      onClick={() => navigate(`/trackawbs/${order.waybill}`)}
                                      className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors cursor-pointer"
                                      title="Track this shipment"
                                    >
                                      <LuTrendingUp size={14} />
                                    </button>
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400 italic">No waybill</span>
                                )}
                              </div>

                              {/* Divider */}
                              <div className="h-6 w-px bg-gray-200 shrink-0" />

                              {/* Tracking ID Input Box & WhatsApp Save and Send */}
                              <div className="flex items-center gap-1.5 shrink-0">
                                <input
                                  type="text"
                                  placeholder="Enter Tracking ID"
                                  value={
                                    orderTrackingInputs[order.order_id] !== undefined
                                      ? orderTrackingInputs[order.order_id]
                                      : getOrderTrackingId(order)
                                  }
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setOrderTrackingInputs((prev) => ({
                                      ...prev,
                                      [order.order_id]: val,
                                    }));
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleSaveAndWhatsApp(order);
                                    }
                                  }}
                                  className="w-36 text-xs px-2.5 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-black bg-white font-mono shadow-inner"
                                  title="Enter tracking ID of the product"
                                />

                                <button
                                  onClick={() => handleSaveAndWhatsApp(order)}
                                  disabled={isSendingWhatsApp === order.order_id}
                                  className="px-2.5 py-1.5 bg-[#25D366] hover:bg-[#20ba5a] text-white rounded text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                                  title="Save Tracking ID and send via WhatsApp"
                                >
                                  {isSendingWhatsApp === order.order_id ? (
                                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <IoLogoWhatsapp className="text-sm shrink-0" />
                                  )}
                                  <span>Save & Send</span>
                                </button>

                                {isTrackingIdChanged(order) && (
                                  <button
                                    onClick={() => handleSaveTrackingOnly(order)}
                                    disabled={isSavingTracking === order.order_id}
                                    className="p-1.5 bg-gray-800 hover:bg-black text-white rounded text-xs shadow-sm transition-all active:scale-95 cursor-pointer"
                                    title="Save Tracking ID only (without WhatsApp)"
                                  >
                                    {isSavingTracking === order.order_id ? (
                                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                      <TbDeviceFloppy size={14} />
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="p-4 min-w-[180px]" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPreviewOrder(order);
                                }}
                                className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 hover:text-blue-800 rounded-lg border border-blue-100 transition-all flex items-center justify-center shadow-3xs cursor-pointer"
                                title="Preview Order Items"
                              >
                                <FiEye size={17} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePrintSingleInvoice(order);
                                }}
                                className="p-2 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg border border-emerald-100 transition-all flex items-center justify-center shadow-3xs cursor-pointer"
                                title="Print Taqwa Invoice"
                              >
                                <TbPrinter size={17} />
                              </button>
                              {!shouldCrossOut && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCancelModalOrder(order);
                                  }}
                                  className="p-2 text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-800 rounded-lg border border-red-100 transition-all flex items-center justify-center shadow-3xs cursor-pointer"
                                  title="Cancel Order"
                                >
                                  <FiXCircle size={17} />
                                </button>
                              )}
                              {shouldCrossOut && (
                                <button
                                  onClick={(e) => handleUndoCancel(e, order)}
                                  className="p-2 text-amber-600 bg-amber-50 hover:bg-amber-100 hover:text-amber-800 rounded-lg border border-amber-100 transition-all flex items-center justify-center shadow-3xs cursor-pointer"
                                  title="Undo Cancellation"
                                >
                                  <FiRotateCcw size={17} />
                                </button>
                              )}
                              <button
                                onClick={(e) => handleDeleteOrder(e, order)}
                                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex items-center justify-center cursor-pointer"
                                title="Delete Order"
                              >
                                <FiTrash2 size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Sleek Shopify Polaris-style Footer Pagination Controls */}
          {status === "all" && !searchTerm && totalCount > pageSize && (
            <div className="p-3.5 sm:p-4 bg-[#fafafb] border-t border-gray-200 flex flex-col sm:flex-row gap-3 items-center justify-between">
              <span className="text-xs font-semibold text-gray-500 text-center sm:text-left">
                Showing <span className="text-gray-900">{(page - 1) * pageSize + 1}</span> to{" "}
                <span className="text-gray-900">{Math.min(page * pageSize, totalCount)}</span> of{" "}
                <span className="text-gray-900">{totalCount}</span> orders
              </span>

              <div className="flex items-center gap-1">
                <button
                  disabled={page === 1}
                  onClick={() => {
                    setPage(page - 1);
                    setSelectedOrderIds([]);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="p-2 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-300 disabled:cursor-not-allowed transition-all shadow-sm flex items-center justify-center"
                >
                  <FiChevronLeft size={16} />
                </button>
                <div className="px-3.5 py-1 text-xs font-bold text-gray-800 bg-white border border-gray-300 rounded shadow-sm">
                  Page {page} / {Math.ceil(totalCount / pageSize)}
                </div>
                <button
                  disabled={page >= Math.ceil(totalCount / pageSize)}
                  onClick={() => {
                    setPage(page + 1);
                    setSelectedOrderIds([]);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="p-2 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-300 disabled:cursor-not-allowed transition-all shadow-sm flex items-center justify-center"
                >
                  <FiChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
      {/* Premium Custom Cancellation Modal */}
      {cancelModalOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm transition-all duration-300">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-100 max-w-md w-full p-6 mx-4 transform transition-all scale-100 flex flex-col gap-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                Cancel Order #{cancelModalOrder.order_id_seq || cancelModalOrder.order_id}
              </h3>
              <p className="text-sm text-gray-500 mt-1.5">
                Please select how you would like to handle the cancellation of this order.
              </p>
            </div>

            {/* List of items in the order for clarity */}
            <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 text-xs max-h-36 overflow-y-auto">
              <span className="font-semibold text-gray-700 block mb-1">Items in this order:</span>
              <ul className="list-disc pl-4 space-y-1 text-gray-600">
                {(Array.isArray(cancelModalOrder.items) ? cancelModalOrder.items : Object.values(cancelModalOrder.items || {})).map((item, idx) => (
                  <li key={idx}>
                    {item.name || 'Product'}{item.size ? ` [${item.size}]` : ''} x {item.quantity || 1}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col gap-2.5 mt-2">
              <button
                disabled={isRestocking}
                onClick={() => executeCancellation(cancelModalOrder, false, false)}
                className="w-full py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold rounded-lg text-sm transition-all duration-200 flex items-center justify-center shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                1. Cancel Normally (No Stock Change)
              </button>

              <button
                disabled={isRestocking}
                onClick={() => executeCancellation(cancelModalOrder, true, false)}
                className="w-full py-2.5 px-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg text-sm transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRestocking ? (
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                ) : null}
                2. Cancel & KEEP STOCK (Update Products Inventory)
              </button>

              <button
                disabled={isRestocking}
                onClick={() => executeCancellation(cancelModalOrder, true, true)}
                className="w-full py-2.5 px-4 bg-black hover:bg-gray-900 text-white font-bold rounded-lg text-sm transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                3. Cancel and Block (Keep Stock & Block Phone)
              </button>
            </div>

            <div className="flex justify-end border-t pt-3 mt-1">
              <button
                disabled={isRestocking}
                onClick={() => setCancelModalOrder(null)}
                className="py-1.5 px-4 border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold rounded-lg text-xs transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Close / Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Quick View / Preview Modal */}
      {previewOrder && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm transition-all duration-300 animate-in fade-in"
          onClick={() => setPreviewOrder(null)}
        >
          <div 
            className="bg-white rounded-3xl shadow-2xl border border-slate-100 max-w-2xl w-full p-6 mx-4 transform transition-all scale-100 flex flex-col gap-5 animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Premium Header */}
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div>
                <span className="text-[10px] font-extrabold text-blue-600 uppercase tracking-widest block">Quick Inspection</span>
                <h3 className="text-xl font-black text-slate-900 mt-1">
                  Order Details #{previewOrder.order_id_seq || previewOrder.order_id}
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Placed on {moment(previewOrder.created_at).format("DD MMMM YYYY [at] h:mm a")}
                </p>
              </div>
              <button 
                onClick={() => setPreviewOrder(null)}
                className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors shadow-xs"
              >
                <FiXCircle size={22} />
              </button>
            </div>


            {/* List of items in a clean wide format with large image */}
            <div className="flex flex-col gap-4 max-h-[460px] overflow-y-auto pr-1.5">
              {(Array.isArray(previewOrder.items) ? previewOrder.items : Object.values(previewOrder.items || {})).map((item, idx) => (
                <div 
                  key={idx} 
                  className="group flex flex-col sm:flex-row gap-5 p-4 bg-slate-50/50 hover:bg-slate-50 border border-slate-100 rounded-2xl shadow-xs hover:shadow-md transition-all duration-300"
                >
                  {/* Large Product Image with Hover Zoom */}
                  <div className="w-full sm:w-36 sm:h-36 h-44 rounded-xl bg-white border border-slate-200 overflow-hidden flex-shrink-0 relative shadow-2xs">
                    <img 
                      src={item.images?.[0] || ""} 
                      alt={item.name || "Product"} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                    />
                    <div className="absolute top-2 left-2 bg-slate-900/80 backdrop-blur-xs text-white text-[9px] font-black px-2 py-0.5 rounded-md tracking-wider">
                      ITEM #{idx + 1}
                    </div>
                  </div>

                  {/* Product Details Section */}
                  <div className="flex-1 flex flex-col justify-between py-1">
                    <div>
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-extrabold block mb-1.5">
                        {item.brand || "TAQWA COLLECTION"}
                      </span>
                      <h4 className="font-extrabold text-slate-800 text-base leading-snug group-hover:text-blue-600 transition-colors duration-200">
                        {item.name}
                      </h4>
                      
                      {/* Option Badges */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        {item.size ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-white text-slate-700 border border-slate-200/60 shadow-3xs">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                            Size: {item.size}
                          </span>
                        ) : null}
                        {item.color || item.brand ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-white text-slate-700 border border-slate-200/60 shadow-3xs">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                            Color: {item.color || item.brand}
                          </span>
                        ) : null}
                      </div>
                    </div>

                    {/* Pricing */}
                    <div className="flex items-center justify-between mt-4 border-t border-slate-100 pt-3">
                      <div className="text-xs text-slate-500 font-medium">
                        <span className="bg-blue-50 text-blue-700 font-bold px-2 py-1 rounded-lg mr-2">
                          Qty {item.quantity || 1}
                        </span>
                        × ₹{(Number(item.priceMap ? item.priceMap[item.size] : item.price) || 0).toLocaleString()}
                      </div>
                      <div className="text-right">
                        <span className="text-[10px] text-slate-400 block font-semibold uppercase tracking-wider">Subtotal</span>
                        <span className="text-base font-black text-slate-900">
                          ₹{((Number(item.priceMap ? item.priceMap[item.size] : item.price) || 0) * (item.quantity || 1)).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Premium Footer Summary */}
            <div className="flex justify-between items-center border-t border-slate-100 pt-4 mt-1">
              <div>
                <span className="text-[10px] text-slate-400 block font-bold uppercase tracking-wider">Total Amount</span>
                <span className="text-xl font-black text-slate-900">
                  ₹{(Number(previewOrder.amount) || 0).toLocaleString()}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPreviewOrder(null)}
                  className="py-2.5 px-5 border border-slate-200 hover:bg-slate-50 text-slate-600 font-bold rounded-2xl text-sm transition-all duration-200 shadow-2xs hover:shadow-xs"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setPreviewOrder(null);
                    navigate(`/order/${previewOrder.order_id}`);
                  }}
                  className="py-2.5 px-5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl text-sm shadow-md hover:shadow-lg transition-all duration-200"
                >
                  View Details
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
